import { CurrencyPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { SecurityService } from '@wsphere/staff/web';
import { Warranty } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, firstValueFrom, from, map, shareReplay, switchMap } from 'rxjs';
import { TenantFeature } from '../../../warranty/config/tenant-features.config';
import { TenantFeatureService, WarrantyWebService } from '../../../warranty/services';
import { formatDateLocalYYYYMMDD } from '../../../warranty/utils/date.utils';
import { compareWarrantyCoverageDisplayOrder } from '../../utils/warranty-coverage-sort.util';

interface CoverageDataSource {
  warranty: Warranty.Model;
  fees: Warranty.WarrantyCoverageFee.Model[];
}

const DISTRIBUTOR_MARKUP_TITLE = 'Distributor Markup';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-warranty-page',
  templateUrl: './warranty-page.component.html',
  standalone: false,
})
export class WarrantyPageComponent implements OnDestroy {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private securitySvc: SecurityService,
    private warrantySvc: WarrantyWebService,
    private http: HttpClient,
    private tenantFeatureSvc: TenantFeatureService
  ) {}

  private currency = new CurrencyPipe('en-US');

  draft = Warranty.Status.DRAFT;
  activated = Warranty.Status.ACTIVATED;
  cancelled = Warranty.Status.CANCELLED;
  expired = Warranty.Status.EXPIRED;
  registered = Warranty.Status.REGISTERED;
  voided = Warranty.Status.VOID;

  //  // Status label mapping for UI display
  getStatusLabel = Warranty.getStatusLabel;

  updatedWarranty$ = new BehaviorSubject<Warranty.Model | null>(null);

  warranty$ = combineLatest([this.route.data, this.updatedWarranty$]).pipe(
    map(([data, warranty]) => {
      const loadedWarranty = data['warranty'] as Warranty.Model;
      return warranty ? { ...loadedWarranty, ...warranty } : loadedWarranty;
    })
  );

  canFinalize$ = this.warranty$.pipe(
    switchMap(
      async warranty =>
        warranty.status === Warranty.Status.DRAFT &&
        warranty.paymentEligible === true &&
        (await this.securitySvc.hasAccess(
          'wrt_CreateWarranty',
          warranty.distributor ? [warranty.distributor.permissionKey] : null
        ))
    )
  );

  canCancelWarranty$ = this.warranty$.pipe(
    switchMap(
      async warranty =>
        await this.securitySvc.hasAccess(
          'wrt_CancelWarranty',
          warranty?.distributor?.permissionKey ? [warranty.distributor.permissionKey] : null
        )
    )
  );

  overlayOpen = false;
  generatingDeclaration = false;

  canUpdate$ = this.warranty$.pipe(
    switchMap(warranty => {
      return this.securitySvc.hasAccess(
        'wrt_UpdateWarranty',
        warranty.distributor?.permissionKey ? [warranty.distributor.permissionKey] : null
      );
    })
  );

  canViewPayments$ = this.warranty$.pipe(
    switchMap(warranty =>
      this.securitySvc.hasAccess(
        'py_ViewSales',
        warranty.distributor?.permissionKey ? [warranty.distributor.permissionKey] : null
      )
    )
  );

  canAdjust$ = this.warranty$.pipe(
    switchMap(warranty => {
      return this.securitySvc.hasAccess(
        'wrt_AdjustWarranty',
        warranty.distributor?.permissionKey ? [warranty.distributor.permissionKey] : null
      );
    })
  );

  @ViewChild('termStartReasonDialog') termStartReasonDialog?: DialogComponent;
  termStartReasonControl = new FormControl('', { nonNullable: true, validators: [Validators.required] });
  termStartReasonSaving = false;
  termStartRevertSaving = false;
  termStartAuditContext: { warrantyId: string; previousTermStart: Date | null; updatedTermStart: Date } | null = null;

  hideFees$ = new BehaviorSubject(true);
  canSeeFees$ = from(this.securitySvc.hasAccess('pol_FeeSetup', null)).pipe(shareReplay(1));

  // Check if tenant has access to declaration page feature
  canShowDeclaration$ = this.tenantFeatureSvc.isFeatureEnabled(TenantFeature.DECLARATION_PAGE).pipe(shareReplay(1));

  coverageData$ = this.warranty$.pipe(
    switchMap(async (warranty): Promise<CoverageDataSource> => {
      const canSeeFees = await firstValueFrom(this.canSeeFees$);
      const fees = canSeeFees
        ? await this.warrantySvc.retrieveWarrantyFees(warranty.id).catch((): Warranty.WarrantyCoverageFee.Model[] => [])
        : [];

      return { warranty, fees };
    }),
    switchMap(({ warranty, fees: allFees }: CoverageDataSource) => {
      return this.hideFees$.pipe(
        map(hideFees => {
          const fees: Warranty.WarrantyCoverageFee.Model[] = hideFees ? [] : allFees;
          const markup = hideFees ? 0 : Number(warranty.markup ?? 0);
          const coverageFeesOnly = fees.filter(fee => !!fee.coverageId && fee.title !== DISTRIBUTOR_MARKUP_TITLE);
          const feeTitles = Array.from(new Set(coverageFeesOnly.map(fee => fee.title))).sort((left, right) =>
            left.localeCompare(right)
          );
          const hasCostColumn = feeTitles.length > 0 || markup > 0;

          const coverages = warranty.coverages
            .map(cov => {
              const coverage = warranty.policy?.coverages?.find(c => c.id === cov.coverageId);

              const included =
                cov.requirement === 'BASE' || cov.requirement === 'NOT_APPLICABLE'
                  ? cov.requirement
                  : cov.requirement.startsWith('cov_')
                    ? 'Individual Add-on'
                    : (warranty.plan?.addons?.find(ad => ad.id === cov.requirement)?.title ?? 'Add-on');

              const rawLiabilityGroups = cov.liabilityGroups as string[] | string | undefined;
              const liabilityGroupIds = Array.isArray(rawLiabilityGroups)
                ? rawLiabilityGroups
                : rawLiabilityGroups
                  ? [rawLiabilityGroups]
                  : [];

              const limits = [
                { limit: 'Base', value: cov.liabilityLimit },
                ...liabilityGroupIds.map(groupId => {
                  const group = warranty.plan?.liabilityGroups?.find(g => g.id === groupId);
                  return {
                    limit: group?.title ?? 'Unknown',
                    value: group?.limit ?? 0,
                  };
                }),
              ]
                .filter(limit => limit.value > 0)
                .sort((a, b) => a.value - b.value);

              const limitsTooltip = limits
                .map(
                  ({ limit, value }) =>
                    `<p>${limit}: ${this.currency.transform(value / 100, 'USD', 'symbol', '1.0-2')}</p>`
                )
                .join('');

              const coverageFees = feeTitles.map(title => {
                return coverageFeesOnly
                  .filter(fee => fee.title === title && fee.coverageId === cov.coverageId)
                  .reduce((acc, fee) => acc + Number(fee.value), 0);
              });

              const cost = coverageFees.reduce((acc, fee) => acc + fee, 0);

              return {
                ...cov,
                fees: coverageFees,
                limit: limits[0],
                limitsTooltip,
                included,
                coverage,
                cost,
              };
            })
            .sort(compareWarrantyCoverageDisplayOrder);

          const feeTotals = feeTitles.map((_, index) =>
            coverages.reduce((acc, cov) => {
              const feePrice = cov.requirement === 'NOT_APPLICABLE' ? 0 : cov.fees[index];
              return acc + feePrice;
            }, 0)
          );

          const costTotal =
            feeTotals.reduce((acc, fee) => {
              return acc + fee;
            }, 0) + markup;

          const retailTotal =
            coverages.reduce((acc, cov) => {
              const feePrice = cov.requirement === 'NOT_APPLICABLE' ? 0 : cov.price;
              return acc + feePrice;
            }, 0) + markup;

          const markupRow =
            markup > 0
              ? [
                  '',
                  DISTRIBUTOR_MARKUP_TITLE,
                  'Warranty',
                  '',
                  '',
                  '',
                  ...(hasCostColumn ? [...feeTitles.map(() => ''), (markup / 100).toFixed(2)] : []),
                  (markup / 100).toFixed(2),
                ]
              : null;

          const rows: string[][] = [
            [
              'Coverage ID',
              'Coverage',
              'Included',
              'Addon ID',
              'Deductible',
              'Liability Limit',
              ...(hasCostColumn ? [...feeTitles, 'Cost'] : []),
              'Retail',
            ],
            ...coverages.map(cov => {
              return [
                cov.coverageId,
                cov.coverage?.title ?? cov.coverageId,
                cov.included,
                cov.requirement !== 'BASE' && cov.requirement !== 'NOT_APPLICABLE' ? cov.requirement : '',
                ((cov.deductible ?? 0) / 100).toFixed(2),
                ((cov.limit?.value ?? 0) / 100).toFixed(2),
                ...(hasCostColumn
                  ? [
                      ...feeTitles.map((_, index) => ((cov.fees[index] ?? 0) / 100).toFixed(2)), //
                      ((cov.cost ?? 0) / 100).toFixed(2),
                    ]
                  : []),
                ((cov.price ?? 0) / 100).toFixed(2),
              ];
            }),
            ...(markupRow ? [markupRow] : []),
          ];

          return {
            columns: 5 + (hasCostColumn ? feeTitles.length + 1 : 0),
            coverages,
            feeTitles,
            markup,
            totals: { fees: feeTotals, cost: costTotal, retail: retailTotal },
            downloadUrl:
              'data:text/csv;charset=utf-8,' + rows.map(row => row.map(val => `"${val}"`).join(',')).join('\n'),
          };
        })
      );
    })
  );

  updateTermStart(warranty: Warranty.Model, date: Date) {
    const previousTermStart = warranty.termStart ? new Date(warranty.termStart) : null;
    this.termStartAuditContext = {
      warrantyId: warranty.id,
      previousTermStart,
      updatedTermStart: date,
    };
    this.termStartReasonControl.reset('');
    this.termStartReasonDialog?.open();
  }

  @HostListener('window:keydown', ['$event'])
  handleGlobalKeydown(event: KeyboardEvent) {
    if (!this.termStartReasonDialog?._open || !this.termStartReasonSaving) return;
    const key = event.key.toLowerCase();
    const isMetaR = key === 'r' && (event.metaKey || event.ctrlKey);
    if (key === 'escape' || key === 'f5' || isMetaR) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  blockUnload(event: BeforeUnloadEvent) {
    if (this.termStartReasonDialog?._open && this.termStartReasonSaving) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  handleTermStartDialogClosed() {
    if (this.termStartReasonSaving) return;
    this.termStartAuditContext = null;
    this.termStartReasonControl.reset('');
  }

  async submitTermStartReason() {
    if (!this.termStartAuditContext) return;

    this.termStartReasonControl.markAsTouched();
    if (this.termStartReasonControl.invalid) return;

    this.termStartReasonSaving = true;
    try {
      const context = this.termStartAuditContext;
      const updated = await this.warrantySvc
        .update(this.termStartAuditContext.warrantyId, { termStart: this.termStartAuditContext.updatedTermStart })
        .catch(({ error }) => {
          this.toastSvc.show({
            type: 'error',
            title: 'Something went wrong',
            text: error.message,
          });
        });

      const updatedWarranty = updated && typeof updated === 'object' ? (updated as { id: string }) : undefined;
      const updatedId = updatedWarranty?.id;
      if (!updatedId) return;
      const formattedTermStart = formatDateLocalYYYYMMDD(this.termStartAuditContext.updatedTermStart);
      const formattedPreviousStart = formatDateLocalYYYYMMDD(this.termStartAuditContext.previousTermStart);
      await this.warrantySvc.logTermStartAdjustmentReason(this.termStartAuditContext.warrantyId, {
        reason: this.termStartReasonControl.value,
        previousTermStart: formattedPreviousStart
          ? formattedPreviousStart
          : this.termStartAuditContext.previousTermStart,
        updatedTermStart: formattedTermStart ? formattedTermStart : this.termStartAuditContext.updatedTermStart,
      });
      this.termStartReasonDialog?.close();
      if (context) {
        this.toastSvc.show({
          type: 'success',
          title: 'Warranty term start updated',
          text: `Successfully updated warranty term start date to ${context.updatedTermStart.toDateString()} and the expiry date has been adjusted accordingly.`,
        });
        await this.refresh(updatedId);
        await new Promise(res => setTimeout(res, 1000));
        window.location.reload();
      }
      this.termStartAuditContext = null;
    } catch (err) {
      const errorMessage =
        (err as { error?: { message?: string } })?.error?.message ??
        (err instanceof Error ? err.message : 'Unknown error');
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to save reason',
        text: errorMessage,
      });
    } finally {
      this.termStartReasonSaving = false;
    }
  }

  async updateCancelled(warranty: Warranty.Model) {
    const updatedWarranty = warranty;
    if (warranty) updatedWarranty.status = Warranty.Status.CANCELLED;
    this.updatedWarranty$.next(updatedWarranty);

    await this.refresh(warranty.id);
  }

  async updateVoided(warranty: Warranty.Model) {
    const updatedWarranty = warranty;
    if (warranty) updatedWarranty.status = Warranty.Status.VOID;
    this.updatedWarranty$.next(updatedWarranty);

    await this.refresh(warranty.id);
  }

  async register(warranty: Warranty.Model) {
    const response = await this.warrantySvc.finalize(warranty.id).catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });
    });

    if (response) {
      this.toastSvc.show({
        type: 'success',
        title: 'Registered Warranty',
        text:
          '<strong>' +
          (warranty?.policy?.title ?? warranty.contractNumber) +
          '</strong> has been successfully registered.',
      });

      await this.refresh(response.id);
    }
  }

  async delete(warranty: Warranty.Model) {
    const response = await this.warrantySvc.delete(warranty.id).catch(({ error }) => {
      this.toastSvc.show({ type: 'error', title: 'Something went wrong', text: error.message });
    });

    if (response) {
      this.toastSvc.show({
        type: 'success',
        title: 'Warranty Deleted',
        text:
          '<strong>' +
          (warranty?.policy?.title ?? warranty.contractNumber) +
          '</strong> has been successfully deleted.',
      });

      await this.router.navigate(['..'], { relativeTo: this.route });
    }
  }

  async downloadDeclaration(warranty: Warranty.Model) {
    if (!warranty?.id) return;

    this.generatingDeclaration = true;
    try {
      // Fetch PDF with authentication headers via HttpClient
      const pdfBlob = await firstValueFrom(
        this.http.get(`/api/warranties/${warranty.id}/declaration`, {
          responseType: 'blob',
        })
      );

      // Create blob URL and try to open in new tab
      const blobUrl = window.URL.createObjectURL(pdfBlob);
      const newTab = window.open(blobUrl, '_blank');

      // Fallback: If pop-up blocked, trigger download instead
      if (!newTab) {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `registration_${warranty.contractNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);

      this.toastSvc.show({
        type: 'success',
        title: 'Registration Generated',
        text: newTab
          ? 'The warranty registration has been opened in a new tab.'
          : 'The warranty registration has been downloaded.',
      });
    } catch (error: unknown) {
      this.toastSvc.show({
        type: 'error',
        title: 'Failed to generate registration',
        text:
          error instanceof Error
            ? error.message
            : 'Unable to generate registration. Please try again or contact support.',
      });
    } finally {
      this.generatingDeclaration = false;
    }
  }

  async refresh(id: string) {
    const updatedWarranty = await this.warrantySvc.getOneById(id).catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });
    });

    if (updatedWarranty) {
      this.updatedWarranty$.next(updatedWarranty);
    }
  }

  ngOnDestroy(): void {
    this.hideFees$.complete();
    this.updatedWarranty$.complete();
  }
}
